/**
 * Satisfaction Rating Component
 *
 * A premium 1-5 rating selector using custom icons designed to match
 * the Have-Done design system. Features smooth animations and a warm,
 * celebration-focused aesthetic.
 */

import { SATISFACTION_LEVELS } from '@have-done/shared';
import { SATISFACTION_ICONS } from './icons/SatisfactionIcons';

interface SatisfactionRatingProps {
  value: number | null;
  onChange: (value: number) => void;
  disabled?: boolean;
  showLabels?: boolean;
}

/**
 * Color classes for each satisfaction level - creates a gradient from muted to vibrant
 */
const LEVEL_COLORS = {
  1: {
    base: 'text-text-tertiary',
    hover: 'hover:text-rose-400',
    selected: 'text-rose-500',
    bg: 'bg-rose-50',
    ring: 'ring-rose-300',
  },
  2: {
    base: 'text-text-tertiary',
    hover: 'hover:text-slate-500',
    selected: 'text-slate-600',
    bg: 'bg-slate-50',
    ring: 'ring-slate-300',
  },
  3: {
    base: 'text-text-tertiary',
    hover: 'hover:text-primary',
    selected: 'text-primary',
    bg: 'bg-primary/10',
    ring: 'ring-primary/50',
  },
  4: {
    base: 'text-text-tertiary',
    hover: 'hover:text-emerald-500',
    selected: 'text-emerald-500',
    bg: 'bg-emerald-50',
    ring: 'ring-emerald-300',
  },
  5: {
    base: 'text-text-tertiary',
    hover: 'hover:text-accent',
    selected: 'text-accent',
    bg: 'bg-accent/15',
    ring: 'ring-accent',
  },
} as const;

export function SatisfactionRating({
  value,
  onChange,
  disabled,
  showLabels = false,
}: SatisfactionRatingProps) {
  return (
    <div
      className="inline-flex items-stretch gap-1 rounded-xl bg-bg-tertiary/50 p-1"
      role="radiogroup"
      aria-label="Satisfaction rating"
    >
      {SATISFACTION_LEVELS.map((level) => {
        const isSelected = value === level.value;
        const Icon = SATISFACTION_ICONS[level.value as keyof typeof SATISFACTION_ICONS];
        const colors = LEVEL_COLORS[level.value as keyof typeof LEVEL_COLORS];

        return (
          <button
            key={level.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={`${level.value} - ${level.label}`}
            disabled={disabled}
            onClick={() => onChange(level.value)}
            className={`
              group relative flex flex-col items-center justify-center
              rounded-lg px-3 py-2 min-w-[52px]
              transition-all duration-200 ease-out
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
              disabled:opacity-50 disabled:cursor-not-allowed
              ${
                isSelected
                  ? `${colors.bg} ${colors.selected} ring-1 ${colors.ring} shadow-sm`
                  : `${colors.base} ${colors.hover} hover:bg-bg-tertiary`
              }
            `}
          >
            <div
              className={`
                relative transition-transform duration-200
                ${isSelected ? 'scale-110' : 'group-hover:scale-110'}
              `}
            >
              <Icon
                filled={isSelected}
                className="h-7 w-7"
                aria-hidden="true"
              />
              {/* Subtle glow effect for selected state */}
              {isSelected && level.value >= 4 && (
                <div
                  className={`
                    absolute inset-0 blur-md opacity-40
                    ${level.value === 5 ? 'bg-accent' : 'bg-emerald-400'}
                  `}
                  aria-hidden="true"
                />
              )}
            </div>
            {showLabels && (
              <span
                className={`
                  mt-1 text-[11px] font-medium tracking-tight
                  transition-colors duration-200
                  ${isSelected ? colors.selected : 'text-text-tertiary group-hover:text-text-secondary'}
                `}
              >
                {level.label}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Compact display version for entry cards
 */
export function SatisfactionDisplay({ value }: { value: number }) {
  const level = SATISFACTION_LEVELS.find((l) => l.value === value);
  if (!level) return null;

  const Icon = SATISFACTION_ICONS[level.value as keyof typeof SATISFACTION_ICONS];
  const colors = LEVEL_COLORS[level.value as keyof typeof LEVEL_COLORS];

  return (
    <span
      className={`inline-flex items-center gap-1.5 ${colors.selected}`}
      title={level.label}
    >
      <Icon filled className="h-4 w-4" aria-label={level.label} />
    </span>
  );
}
